// small colour maths for custom accents and backgrounds, works on #rrggbb strings

export type RGB = [number, number, number];

export function parseHex(hex: string): RGB | null {
  const match = /^#?([0-9a-f]{6})$/i.exec((hex || "").trim());
  if (!match) return null;
  const value = parseInt(match[1], 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

export function toHex(rgb: RGB): string {
  return "#" + rgb.map((part) => Math.round(Math.max(0, Math.min(255, part))).toString(16).padStart(2, "0")).join("");
}

// t = 0 gives a, t = 1 gives b
export function mix(a: RGB, b: RGB, t: number): RGB {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
}

export function rgba(rgb: RGB, alpha: number): string {
  return `rgba(${Math.round(rgb[0])}, ${Math.round(rgb[1])}, ${Math.round(rgb[2])}, ${alpha})`;
}

export function luminance(rgb: RGB): number {
  const [r, g, b] = rgb.map((part) => {
    const unit = part / 255;
    return unit <= 0.03928 ? unit / 12.92 : ((unit + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrast(a: RGB, b: RGB): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

// h 0-360, s and l 0-1
export function hsl(h: number, s: number, l: number): RGB {
  const k = (n: number) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return [f(0) * 255, f(8) * 255, f(4) * 255];
}

// push a colour toward black or white until it reads against a background
export function readable(color: RGB, against: RGB, ratio = 3.2): RGB {
  const toward: RGB = luminance(against) > 0.4 ? [0, 0, 0] : [255, 255, 255];
  let out = color;
  for (let step = 0; step < 20 && contrast(out, against) < ratio; step++) out = mix(out, toward, 0.08);
  return out;
}
